import { Box } from '@mui/system';
import { GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import {
  Badge,
  Button,
  ToggleButton,
  DialogActions,
  DialogContent,
  DialogContentText,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { useState, useEffect, useRef } from 'react';
import { $authHost } from 'app/configs/axiosConfig';
import { useQueryClient } from '@tanstack/react-query';
import FileUploadDialog from '../components/FileUploadDialog';
import AffiliateLeadEditDialog from './AffiliateLeadEditDialog';
import { useSnackbar } from 'notistack';
import AppSimpleDialog from 'app/shared-components/dialogs/AppSimpleDialog';
import StyledDataGrid from 'app/shared-components/GridComponents/StyledDataGrid';
import { FiltersFormValues } from '../components/FiltersForm';
import useTablePagination from 'src/app/hooks/useTablePagination';
import useSortingProcessor from 'src/app/hooks/useSortingProcessor';
import useFiltersProcessor from 'src/app/hooks/useFiltersProcessor';
import { Divider } from '@mui/material';
import {
  useFilteredAffiliateLeads,
  useAffiliateLeadsStatusQuery,
} from 'src/app/queries/affiliate-leads.queries';
import { useQuery } from '@tanstack/react-query';
import useAffiliateLeadsColDef from './useAffiliateLeadsColDef';
import AffiliateLeadsFiltersDialog from './AffiliateLeadsFiltersDialog';
import { type AffiliateLead } from './types';
import { PROCESSOR_OPTIONS } from 'src/app/consts/processorOptions';
import useAuthUser from 'src/app/hooks/useAuthUser';

interface AffiliateLeadsStatusResponse {
  data: {
    isUploadInProgress: boolean;
    totalUploaded: number;
    totalMarkedForUpload: number;
    totalDraft: number;
    totalFailed: number;
  };
}

const AffiliateLeads = () => {
  const { processSorting } = useSortingProcessor();
  const { processFilters } = useFiltersProcessor();
  const user = useAuthUser();
  const isManager = user?.role === 'manager';

  const [filtersDialog, setFiltersDialog] = useState<boolean>(false);
  const [filters, setFilters] = useState<FiltersFormValues>();

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'contactDate',
      sort: 'desc',
    },
  ]);

  const {
    pageSizes,
    defaultPageSize,
    paginationModel,
    totalPages,
    setPaginationModel,
    setTotalPages,
  } = useTablePagination();

  const apiRef = useGridApiRef();

  const [isFileUploadDialogOpen, setIsFileUploadDialogOpen] = useState<boolean>(false);
  const [isUploadLeadsButtonDisabled, setIsUploadLeadsButtonDisabled] = useState<boolean>(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [selectedLead, setSelectedLead] = useState<AffiliateLead | null>(null);
  const [uploadConfirmationOpen, setUploadConfirmationOpen] = useState<boolean>(false);
  const [selectedProcessor, setSelectedProcessor] = useState<string>('');
  const [stats, setStats] = useState<{
    totalUploaded: number;
    totalMarkedForUpload: number;
    totalDraft: number;
    totalFailed: number;
  }>({
    totalUploaded: 0,
    totalMarkedForUpload: 0,
    totalDraft: 0,
    totalFailed: 0,
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const { enqueueSnackbar } = useSnackbar() as {
    enqueueSnackbar: (message: string, options?: { variant?: string }) => void;
  };
  const queryClient = useQueryClient();
  const pollingInterval = useRef<number | null>(null);

  const affiliateLeadsQuery = useFilteredAffiliateLeads({
    skip: paginationModel.page * paginationModel.pageSize,
    take: paginationModel.pageSize,
    orderBy: processSorting(sortModel),
    where: filters ? processFilters(filters) : {},
  });

  const statusQuery = useAffiliateLeadsStatusQuery();

  const draftOrFailedLeadsQuery = useQuery({
    queryKey: ['draftOrFailedAffiliateLeads'],
    queryFn: async () => {
      const response = await $authHost.post('/affiliate-lead/filter', {
        skip: 0,
        take: 4, // Show first 4 leads for preview
        where: { status: { in: ['Draft', 'Failed'] } },
        orderBy: { contactDate: 'desc' },
      });
      return response.data as {
        data: {
          items: AffiliateLead[];
          totalCount: number;
        };
      };
    },
    enabled: uploadConfirmationOpen,
  });

  const draftOrFailedLeads = draftOrFailedLeadsQuery.data?.data.items ?? [];
  const totalDraftOrFailedLeads = draftOrFailedLeadsQuery.data?.data.totalCount ?? 0;

  const leads = (affiliateLeadsQuery.data?.data.items ?? []) as AffiliateLead[];
  const isLoading = affiliateLeadsQuery.isLoading;
  const isFetching = affiliateLeadsQuery.isFetching;

  useEffect(() => {
    void initialCheck();

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    if (affiliateLeadsQuery.data?.data.totalCount) {
      setTotalPages(affiliateLeadsQuery.data?.data.totalCount);
    }
  }, [paginationModel.pageSize, isLoading, filters, affiliateLeadsQuery.data?.data.totalCount]);

  useEffect(() => {
    if (isFetching) return;
    const timer = setTimeout(() => {
      void apiRef.current.autosizeColumns({ includeOutliers: true });
    }, 100);
    return () => clearTimeout(timer);
  }, [isLoading, isFetching]);

  // Handle status query data updates
  useEffect(() => {
    if ((statusQuery.data as AffiliateLeadsStatusResponse)?.data) {
      const data = (statusQuery.data as AffiliateLeadsStatusResponse).data;
      setIsUploadLeadsButtonDisabled(data.isUploadInProgress);
      setStats({
        totalUploaded: data.totalUploaded,
        totalMarkedForUpload: data.totalMarkedForUpload,
        totalDraft: data.totalDraft,
        totalFailed: data.totalFailed,
      });
    }
  }, [statusQuery.data]);

  const checkIfMarkedForUpload = async () => {
    try {
      const response = await $authHost.post('/affiliate-lead/check-marked-for-upload');
      const data = (response.data as AffiliateLeadsStatusResponse).data;
      setIsUploadLeadsButtonDisabled(data.isUploadInProgress);
      setStats({
        totalUploaded: data.totalUploaded,
        totalMarkedForUpload: data.totalMarkedForUpload,
        totalDraft: data.totalDraft,
        totalFailed: data.totalFailed,
      });
      return data.isUploadInProgress;
    } catch (error) {
      return false;
    }
  };

  const startPolling = () => {
    pollingInterval.current = window.setInterval(async () => {
      const isDisabled = await checkIfMarkedForUpload();
      if (!isDisabled) {
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
      }
    }, 10000);
  };

  const initialCheck = async () => {
    const isDisabled = await checkIfMarkedForUpload();
    if (isDisabled) {
      startPolling();
    }
  };

  const uploadLeads = async () => {
    if (!selectedProcessor) {
      enqueueSnackbar('Please select a target processor.', {
        variant: 'warningToast',
      });
      return;
    }

    setUploadConfirmationOpen(false);
    try {
      const checkResponse = await $authHost.post('/affiliate-lead/check-marked-for-upload');
      const isUploading = (checkResponse.data as AffiliateLeadsStatusResponse).data
        .isUploadInProgress;

      if (isUploading) {
        setIsUploadLeadsButtonDisabled(true);
        enqueueSnackbar('Upload is already in progress. Please wait until it completes.', {
          variant: 'warningToast',
        });
        return;
      }

      const response = await $authHost.post('/affiliate-lead/upload', {
        uploadTarget: selectedProcessor,
      });

      if (response.status >= 200 && response.status < 300) {
        enqueueSnackbar(`Upload of ${totalDraftOrFailedLeads} leads to ${selectedProcessor} has started.`, {
          variant: 'successToast',
        });
        setIsUploadLeadsButtonDisabled(true);

        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
        }
        pollingInterval.current = window.setInterval(async () => {
          const uploadStatus = await checkIfMarkedForUpload();
          if (!uploadStatus) {
            if (pollingInterval.current) {
              clearInterval(pollingInterval.current);
              pollingInterval.current = null;
            }
          }
        }, 10000);
      } else {
        throw new Error((response.data as { message?: string })?.message || 'Unknown error');
      }

      await new Promise((resolve) => setTimeout(resolve, 3000));

      setPaginationModel({ ...paginationModel, page: 0 });
      void affiliateLeadsQuery.refetch();
    } catch (error) {
      enqueueSnackbar(`Failed to upload leads`, {
        variant: 'errorToast',
      });
    }
  };

  const updateLeadApproval = async (lead: AffiliateLead) => {
    try {
      const response = await $authHost.patch(`/affiliate-lead/${lead.id}/approve`);
      if (response.status >= 200 && response.status < 300) {
        enqueueSnackbar('Lead status updated successfully.', {
          variant: 'successToast',
        });
        // Update the local state instead of refetching
        const updatedLeads = leads.map((item) =>
          item.id === lead.id ? { ...item, isApproved: !item.isApproved } : item,
        );
        // Update the query cache directly
        queryClient.setQueryData(
          [
            'affiliateLeads',
            {
              skip: paginationModel.page * paginationModel.pageSize,
              take: paginationModel.pageSize,
              orderBy: processSorting(sortModel),
              where: filters ? processFilters(filters) : {},
            },
          ],
          (oldData: any) =>
            oldData ? { ...oldData, data: { ...oldData.data, items: updatedLeads } } : oldData,
        );
      } else {
        enqueueSnackbar('Failed to update lead status.', {
          variant: 'errorToast',
        });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      enqueueSnackbar(`Sorry, something went wrong. ${errorMessage}`, {
        variant: 'errorToast',
      });
    }
  };

  const columns = useAffiliateLeadsColDef({
    onIsApprovedChange: (lead: AffiliateLead) => void updateLeadApproval(lead),
  });

  return (
    <div className="w-full p-24 flex flex-col h-full max-h-full">
      <div className="mb-16 flex justify-between">
        <h1 className="font-semibold text-24">Affiliate Leads</h1>
        <div>
          <Badge
            color="secondary"
            invisible={!filters || filters.filters.length === 0}
            badgeContent={filters?.filters.length}
            max={10}
            sx={{
              '& .MuiBadge-badge': {
                right: 5,
                top: 5,
              },
            }}
          >
            <ToggleButton
              value={0}
              className={`rounded-full border-0 p-10 ${
                filtersDialog ? 'text-blue-primary bg-blue-primary bg-opacity-10' : null
              } `}
              onClick={() => setFiltersDialog(true)}
            >
              <FuseSvgIcon>material-outline:tune</FuseSvgIcon>
            </ToggleButton>
          </Badge>
          <Button
            className=" ml-16 p-10 w-fit min-w-fit "
            onClick={() => {
              void affiliateLeadsQuery.refetch();
            }}
          >
            <FuseSvgIcon>material-outline:cached</FuseSvgIcon>
          </Button>
          {!isManager && (
            <>
              <Tooltip
                title={
                  isUploadLeadsButtonDisabled
                    ? 'Upload is in progress. The button will become active once it is finished'
                    : 'Upload up to 2000 leads to HubSpot. This process can take up to 15 minutes to complete.'
                }
                placement="top"
              >
                <span>
                  <Button
                    className="ml-16 w-fit min-w-fit border-2 border-gray-500 hover:border-gray-700 border-solid px-16 py-8"
                    onClick={() => {
                      setUploadConfirmationOpen(true);
                    }}
                    disabled={isUploadLeadsButtonDisabled}
                  >
                    <FuseSvgIcon>material-outline:upload</FuseSvgIcon>
                    {isUploadLeadsButtonDisabled ? 'Sending...' : 'Send Leads'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                className="ml-16 text-white font-500 bg-blue-primary hover:bg-blue-primary-hover px-16 py-8 disabled:bg-gray-400 disabled:text-opacity-60 gap-8"
                onClick={() => setIsFileUploadDialogOpen(true)}
              >
                <FuseSvgIcon>material-outline:upload_file</FuseSvgIcon>
                Upload file
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Statistics Section */}
      <div className="mb-16 grid grid-cols-4 gap-16">
        <div className="bg-white rounded-lg p-16 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-4">Total Uploaded</p>
              <p className="text-2xl font-semibold text-green-600">{stats.totalUploaded}</p>
            </div>
            <FuseSvgIcon className="text-green-500" size={24}>
              material-outline:cloud_done
            </FuseSvgIcon>
          </div>
        </div>

        <div className="bg-white rounded-lg p-16 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-4">Marked for Upload</p>
              <p className="text-2xl font-semibold text-blue-600">{stats.totalMarkedForUpload}</p>
            </div>
            <FuseSvgIcon className="text-blue-500" size={24}>
              material-outline:schedule
            </FuseSvgIcon>
          </div>
        </div>

        <div className="bg-white rounded-lg p-16 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-4">Draft</p>
              <p className="text-2xl font-semibold text-gray-600">{stats.totalDraft}</p>
            </div>
            <FuseSvgIcon className="text-gray-500" size={24}>
              material-outline:edit
            </FuseSvgIcon>
          </div>
        </div>

        <div className="bg-white rounded-lg p-16 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-4">Failed</p>
              <p className="text-2xl font-semibold text-red-600">{stats.totalFailed}</p>
            </div>
            <FuseSvgIcon className="text-red-500" size={24}>
              material-outline:error_outline
            </FuseSvgIcon>
          </div>
        </div>
      </div>

      <FileUploadDialog
        open={isFileUploadDialogOpen}
        onClose={() => setIsFileUploadDialogOpen(false)}
        onUploadSuccess={() => {
          setPaginationModel({ ...paginationModel, page: 0 });
          void affiliateLeadsQuery.refetch();
        }}
      />
      <AppSimpleDialog
        open={uploadConfirmationOpen}
        handleClose={() => {
          setUploadConfirmationOpen(false);
          setSelectedProcessor('');
        }}
        title="Send leads"
        size="sm"
        scroll="paper"
      >
        <Divider />
        <DialogContent className="px-24 relative">
          {/* Selected Leads Section */}
          <div className="mb-24">
            <DialogContentText className="mb-12 font-semibold">
              Selected {totalDraftOrFailedLeads} Leads:
            </DialogContentText>
            <div className="bg-gray-50 rounded-lg p-16 max-h-200 overflow-y-auto">
              {draftOrFailedLeadsQuery.isLoading ? (
                <div className="text-center text-gray-500">Loading leads...</div>
              ) : draftOrFailedLeads.length > 0 ? (
                <div className="space-y-8">
                    {draftOrFailedLeads.map((lead) => (
                    <div key={lead.id} className="flex items-center">
                      <FuseSvgIcon size={16} className="mr-8 text-gray-400">
                        material-outline:person
                      </FuseSvgIcon>
                      <span className="text-sm">
                        <strong>
                          {lead.firstName} {lead.lastName}
                        </strong>{' '}
                        ({lead.email})
                      </span>
                    </div>
                  ))}
                    {totalDraftOrFailedLeads > 4 && (
                    <div className="text-sm text-gray-500 italic">
                        And {totalDraftOrFailedLeads - 4} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500">No draft leads found</div>
              )}
            </div>
          </div>

          {/* Processor Selection Section */}
          <DialogContentText id="alert-dialog-description" className="mb-20">
            Select Destination:
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="processor-select-label">Processor</InputLabel>
            <Select
              labelId="processor-select-label"
              id="processor-select"
              value={selectedProcessor}
              label="Processor"
              onChange={(event) => setSelectedProcessor(event.target.value)}
            >
              {PROCESSOR_OPTIONS.map((processor) => (
                <MenuItem key={processor.value} value={processor.value}>
                  {processor.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className="flex items-center mr-24 ml-12 my-20 p-0">
          <Button
            onClick={() => {
              setUploadConfirmationOpen(false);
              setSelectedProcessor('');
            }}
            className="flex items-center text-gray-600 font-500 bg-gray-50 hover:bg-gray-200 px-16 py-8 mr-16"
          >
            Back
          </Button>
          <Button
            onClick={() => void uploadLeads()}
            disabled={!selectedProcessor || totalDraftOrFailedLeads === 0}
            className="text-white font-500 bg-blue-primary hover:bg-blue-primary-hover px-16 py-8 disabled:bg-gray-400 disabled:text-opacity-60"
          >
            Send {totalDraftOrFailedLeads} leads
          </Button>
        </DialogActions>
      </AppSimpleDialog>
      <AffiliateLeadEditDialog
        open={isEditDialogOpen}
        handleClose={() => setIsEditDialogOpen(false)}
        lead={selectedLead}
        onDelete={() => {
          setPaginationModel({ ...paginationModel, page: 0 });
          void affiliateLeadsQuery.refetch();
        }}
        onSave={() => {
          setPaginationModel({ ...paginationModel, page: 0 });
          void affiliateLeadsQuery.refetch();
        }}
      />
      <Box className="flex flex-col table-base table-inner-scroll">
        <StyledDataGrid
          disableColumnMenu
          disableVirtualization
          apiRef={apiRef}
          columns={columns}
          rows={leads}
          pageSizeOptions={pageSizes}
          onPaginationModelChange={setPaginationModel}
          paginationModel={paginationModel}
          paginationMode="server"
          sortingMode="server"
          initialState={{
            pagination: { paginationModel: { pageSize: defaultPageSize } },
          }}
          loading={isLoading || isFetching}
          rowCount={totalPages}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            setSortModel(newSortModel);
            setPaginationModel({ ...paginationModel, page: 0 });
          }}
          onRowClick={(params) => {
            if (!isManager && ['Draft', 'Failed'].includes((params.row as AffiliateLead).status)) {
              setSelectedLead(params.row as AffiliateLead);
              setIsEditDialogOpen(true);
            }
          }}
        />
      </Box>
      <AffiliateLeadsFiltersDialog
        open={filtersDialog}
        selectedFilters={filters}
        onClose={() => setFiltersDialog(false)}
        onFiltersApply={(f) => {
          setFilters(f);
          setFiltersDialog(false);
        }}
        onFiltersClear={() => {
          setFilters(undefined);
          setFiltersDialog(false);
        }}
      />
    </div>
  );
};

export default AffiliateLeads;
